import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNuxtApp, useRoute } from 'nuxt/app';
import { useCategoryStore } from './category';
import { useConfigStore } from './config';
import { useProductStore } from './product';
import { useProductsStore } from './products';
import { useModalStore } from './modal';
import { useOrderStore } from './order';
import { useQueueStore } from './queue';
import { setErrorNotification } from '~~/common/utils/helper';
import { isApiError } from '~~/common/utils/apiError';
import { asStringCookie } from '~~/common/utils/cookie';
import { useGdprStore } from '~~/shop/app/stores/gdpr';
import { useLoyaltyCardStore } from '~~/shop/app/stores/loyaltyCard';
import { LT_ANON, LT_ECOM } from '~~/common/config/LoginTypeConfig';
import type { DeepPartial, ResponseDataOpStrict, ResponseDataStrict, Schema } from '~~/common/types/api';

type UserBaseType = DeepPartial<Schema<'MeModel'>>;

export type ShoppingListResponseType = ResponseDataStrict<'/api/v2/me/shopping_lists', 'get'>;

export type ShoppingListItemsResponseType = ResponseDataOpStrict<'get_shopping_list_item_collection'>;

export interface UserType extends Omit<UserBaseType, 'credential' | 'credentialModel'> {
  mailingInfos?: Schema<'MeModel.MailingAddressModel'>[];
  mailingInfosCount?: number;
  deliveryInfos?: Schema<'MeModel.DeliveryAddressModel'>[];
  deliveryInfosCount?: number;
  billingInfos?: Schema<'MeModel.BillingAddressModel'>[];
  billingInfosCount?: number;
  shoppingLists?: ShoppingListResponseType | null;
  children?: Schema<'ChildModel'>[];
}

interface ShoppingListsPagination {
  itemsPerPage: number;
  page: number;
}

interface FetchShoppingListItemsPayload {
  shoppingListId: string | number;
  page: number;
  itemsPerPage: number;
  abortSignal?: AbortSignal;
}

interface FetchShoppingListsPayload {
  itemsPerPage?: number;
  page?: number;
  mergeOldAndNewItems?: boolean;
  updatePagination?: boolean;
}


function createInitialUserData (): UserType {
  return {
    personalInfo: {},
    mailingInfos: [],
    mailingInfosCount: 0,
    deliveryInfos: [],
    deliveryInfosCount: 0,
    billingInfos: [],
    billingInfosCount: 0,
    counters: {},
    id: null,
    shoppingLists: null,
  };
}

export const useUserStore = defineStore('user', () => {
  const productsStore = useProductsStore();
  const productStore = useProductStore();
  const gdprStore = useGdprStore();
  const modalStore = useModalStore();
  const orderStore = useOrderStore();
  const loyaltyCardStore = useLoyaltyCardStore();
  const queueStore = useQueueStore();
  const configStore = useConfigStore();
  const categoryStore = useCategoryStore();
  const nuxtApp = useNuxtApp();
  const route = useRoute();

  //data
  const data = ref<UserType>(createInitialUserData());

  const isItemsPerPageSectionOpened = ref(false);
  const forceLogoutToRedirect = ref(false);
  const loginType = ref('');
  // showed at once the alert modal before logged in the user, we store info this variable ->
  const showAlcoholAlertModalBeforeLogIn = ref(false);
  const shoppingListNames = ref<Schema<'MeModel.BaseShoppingListModel'>[]>([]);
  const shoppingListsPagination = ref<ShoppingListsPagination>({
    itemsPerPage: 6,
    page: 1,
  });
  const shoppingListSessionData = ref<(string | number)[]>([]);

  //actions
  function toggleIsItemsPerPageSectionOpened () {
    isItemsPerPageSectionOpened.value = !isItemsPerPageSectionOpened.value;
  }

  function resetShoppingListSessionData () {
    shoppingListSessionData.value = [];
  }

  function addShoppingListToSessionData (listId: string | number) {
    if (!shoppingListSessionData.value.includes(listId)) {
      shoppingListSessionData.value.push(listId);
    }
  }

  function removeShoppingListFromSessionData (listId: string | number) {
    shoppingListSessionData.value = shoppingListSessionData.value.filter(id => id !== listId);
  }

  function setUserData (newData: Partial<UserType>) {
    data.value = { ...data.value, ...newData };
  }

  function resetUserData () {
    data.value = createInitialUserData();
  }

  function setLoginType (newLoginType: string) {
    loginType.value = newLoginType;
  }

  function setForceLogoutToRedirect () {
    forceLogoutToRedirect.value = true;
  }

  function setShoppingLists (newData: ShoppingListResponseType | null) {
    data.value.shoppingLists = newData;
  }

  function updateShoppingLists (newData: ShoppingListResponseType) {
    if (!data.value.shoppingLists) {
      data.value.shoppingLists = { ...newData };
      return;
    }
    data.value.shoppingLists.results = data.value.shoppingLists.results.concat(newData.results);
    data.value.shoppingLists.currentPage = newData.currentPage;
  }

  function setShoppingListsPagination (newData: ShoppingListsPagination) {
    shoppingListsPagination.value = newData;
  }

  function setShowAlcoholAlertModalBeforeLogIn (status: boolean) {
    showAlcoholAlertModalBeforeLogIn.value = status;
  }

  async function refreshProductsData () {
    const { $logger } = nuxtApp;
    try {
      const productData = productStore.data;
      if (productData?.id) {
        productStore.fetchMainProduct({
          identifierType: 'id',
          id: productData.id,
          params: undefined,
          specialErrorMessage: undefined,
        });
      }
      await productsStore.throttledRefresh();
      productsStore.resetFilterBy();
    } catch (err) {
      $logger.error(err);
    }
  }

  async function fetchUser ({ refreshData }: { refreshData?: boolean } = { refreshData: true }) {
    if (import.meta.server) {
      return await fetchUserRequest({ refreshData });
    } else {
      return await queueStore.add({
        action: fetchUserRequest,
        actionName: 'user/fetchUserRequest',
        payload: { refreshData },
        canStartSecondAction: true,
        canRunSimultaneous: true,
      });
    }
  }

  async function fetchUserRequest ({ refreshData }: { refreshData?: boolean } = { refreshData: true }) {
    const {
      $api,
      $cookies,
      $logger,
    } = nuxtApp;
    const loginTypeFromCookie = asStringCookie($cookies.get('login_type'));
    const loggedIn = loginTypeFromCookie !== LT_ANON;
    const isUserLoginTypeChanged = loginTypeFromCookie !== loginType.value;
    if (loginTypeFromCookie) {
      setLoginType(loginTypeFromCookie);
    }

    if (loggedIn) {
      try {
        const result = await $api.$get<Partial<UserType> | null>('/me');
        if (result != null) {
          setUserData(result);

          await Promise.all([orderStore.fetchOrder({
            id: 'current',
            canStartSecondAction: true,
            canRunSimultaneous: true,
            fetchFoodCheckout: __AW_IS_SHOP__,
            fetchNonfoodCheckoutIfNFCartNotEmpty: __AW_IS_SHOP__,
            mode: /.*?\/(basket|kosar|checkout)/gi.test(route.fullPath) ? undefined : 'MINICART',
          }), orderStore.fetchOrderModification()]);
        } else {
          resetUserData();
          loyaltyCardStore.resetLoyaltyData();
          setLoginType(LT_ANON);

          await orderStore.fetchOrder({
            id: 'current',
            canStartSecondAction: true,
            canRunSimultaneous: true,
            fetchFoodCheckout: __AW_IS_SHOP__,
            fetchNonfoodCheckoutIfNFCartNotEmpty: __AW_IS_SHOP__,
            mode: /.*?\/(basket|kosar|checkout)/gi.test(route.fullPath) ? undefined : 'MINICART',
          });
        }

        const proms: Promise<unknown>[] = [];

        // refresh product, category, deliveryArea after fetchUser
        if (refreshData) {
          proms.push(refreshProductsData());
        }

        if (__AW_IS_SHOP__ && isEcomUser.value) {
          proms.push(gdprStore.fetchAcceptance().then(() => {
            if (gdprStore?.acceptance?.updateNeeded) {
              modalStore.openGdprAcceptanceModal();
            } else if (configStore.isSalesForceLogin && !data.value?.personalInfo?.birthDate && !data.value?.personalInfo?.isB2B) {
              modalStore.openProfileModal();
            }
          }));
        }
        await Promise.all(proms);
      } catch (error: unknown) {
        resetUserData();
        loyaltyCardStore.resetLoyaltyData();
        setLoginType(LT_ANON);

        // refresh product, category, deliveryArea after fetchUser
        const productData = productStore.data;
        if (productData?.id) {
          productStore.fetchMainProduct({
            identifierType: 'id',
            id: productData.id,
            params: undefined,
            specialErrorMessage: undefined,
          });
        }
        productsStore.resetFilterBy();
        await Promise.all([
          orderStore.fetchOrder({
            id: 'current',
            canStartSecondAction: true,
            canRunSimultaneous: true,
            fetchFoodCheckout: __AW_IS_SHOP__,
            fetchNonfoodCheckoutIfNFCartNotEmpty: __AW_IS_SHOP__,
          }),
          productsStore.throttledRefresh(),
          categoryStore.fetchCategories()]);

        const errorStatus: number | undefined = isApiError(error) ? error.response?.status : undefined;
        // 401 (expired/invalid token) and 403 (forbidden) both mean the logged-in session is gone
        // and we've just fallen back to anonymous — the user's in-flight queued cart actions are no
        // longer valid, so drop them silently. Any other status is an unexpected failure worth logging.
        if (errorStatus === 401 || errorStatus === 403) {
          queueStore.removeAllElementFromQueue(false);
        } else {
          $logger.error(error);
        }
      }
    } else {
      resetUserData();
      loyaltyCardStore.resetLoyaltyData();
      await Promise.all([orderStore.fetchOrder({
        id: 'current',
        canStartSecondAction: true,
        canRunSimultaneous: true,
        fetchFoodCheckout: __AW_IS_SHOP__,
        fetchNonfoodCheckoutIfNFCartNotEmpty: __AW_IS_SHOP__,
      }), isUserLoginTypeChanged ? productsStore.throttledRefresh() : undefined]);
      // refresh product, category, deliveryArea after fetchUser
      const productData = productStore.data;
      if (productData?.id && refreshData) {
        productStore.fetchMainProduct({
          identifierType: 'id',
          id: productData.id,
          params: undefined,
          specialErrorMessage: undefined,
        });
      }
      if (refreshData) {
        productsStore.resetFilterBy();
      }
    }
  }

  async function fetchShoppingListItems ({
    shoppingListId,
    page,
    itemsPerPage,
    abortSignal,
  }: FetchShoppingListItemsPayload) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      return await $api.$get<ShoppingListItemsResponseType>(`/me/shopping_lists/${shoppingListId}/items?page=${page}&itemsPerPage=${itemsPerPage}`, {
        signal: abortSignal,
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      }
      return {};
    }
  }

  async function fetchShoppingLists ({
    itemsPerPage,
    page,
    mergeOldAndNewItems,
    updatePagination,
  }: FetchShoppingListsPayload = {
    itemsPerPage: shoppingListsPagination.value.itemsPerPage,
    page: shoppingListsPagination.value.page,
    mergeOldAndNewItems: false,
    updatePagination: true,
  }) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result: ShoppingListResponseType = __AW_IS_SHOP__
        ? await $api.$get<ShoppingListResponseType>(`/me/shopping_lists?page=${page}&itemsPerPage=${itemsPerPage}&responseWithItems=0`)
        : { results: [], currentPage: 0, pageCount: 0, itemCount: 0 };
      if (mergeOldAndNewItems) {
        updateShoppingLists(result);
      } else if (updatePagination) {
        setShoppingLists(result);
      } else {
        setShoppingLists({
          ...result,
          pageCount: Math.ceil(result.itemCount / shoppingListsPagination.value.itemsPerPage),
        });
      }
      if (updatePagination) {
        setShoppingListsPagination({
          itemsPerPage: shoppingListsPagination.value.itemsPerPage,
          page: result.currentPage,
        });
      }
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  async function fetchShoppingListNames () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      shoppingListNames.value = await $api.$get<Schema<'MeModel.BaseShoppingListModel'>[]>('/me/shopping_list_names');
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  async function fetchPersonalInfo () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      data.value.personalInfo = await $api.$get<UserType['personalInfo']>('/me/personal_info');
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  async function fetchChildrenInfo () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<{ children?: UserType['children'] }>('/me/children');
      data.value.children = result?.children;
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  async function fetchBillingInfos () {
    const {
      $api,
      $logger,
    } = nuxtApp;

    try {
      const result = await $api.$get<UserType['billingInfos']>('/me/billing_infos?page=1');
      data.value.billingInfos = result;
      data.value.billingInfosCount = result?.length;
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  async function fetchDeliveryInfos () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<UserType['deliveryInfos']>('/me/delivery_infos?page=1');
      data.value.deliveryInfos = result;
      data.value.deliveryInfosCount = result?.length;
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  async function fetchMailingInfos () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<UserType['mailingInfos']>('/me/mailing_infos?page=1');
      data.value.mailingInfos = result;
      data.value.mailingInfosCount = result?.length;
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 403) {
        $logger.error(error);
      }
    }
  }

  //getters
  const isEcomUser = computed(() => loginType.value === LT_ECOM);
  const isAnonUser = computed(() => loginType.value === LT_ANON);

  return {
    data,
    isItemsPerPageSectionOpened,
    forceLogoutToRedirect,
    loginType,
    showAlcoholAlertModalBeforeLogIn,
    shoppingListNames,
    shoppingListsPagination,
    shoppingListSessionData,
    toggleIsItemsPerPageSectionOpened,
    resetShoppingListSessionData,
    addShoppingListToSessionData,
    removeShoppingListFromSessionData,
    setUserData,
    resetUserData,
    setLoginType,
    setForceLogoutToRedirect,
    setShoppingLists,
    updateShoppingLists,
    setShoppingListsPagination,
    setShowAlcoholAlertModalBeforeLogIn,
    refreshProductsData,
    fetchUser,
    fetchUserRequest,
    fetchShoppingListItems,
    fetchShoppingLists,
    fetchShoppingListNames,
    fetchPersonalInfo,
    fetchChildrenInfo,
    fetchBillingInfos,
    fetchDeliveryInfos,
    fetchMailingInfos,
    isEcomUser,
    isAnonUser,
  };
});
